/** Marcador e helpers de anexo de mídia do board-chat. */

export const ATTACHMENT_MARKER = '__attachment__:';

export const FOTO_EXT = new Set(['jpg', 'jpeg', 'jfif', 'png', 'heic']);
export const VIDEO_EXT = new Set(['mp4', 'mov', 'avi', 'mkv']);
export const AUDIO_EXT = new Set(['webm', 'ogg', 'mp3', 'm4a', 'wav', 'opus']);
export const ACCEPT_MEDIA =
  '.jpg,.jpeg,.jfif,.png,.heic,.mp4,.mov,.avi,.mkv,image/jpeg,image/png,image/heic,video/mp4,video/quicktime,video/x-msvideo,video/x-matroska';

export const MAX_FILES_PER_DRAFT = 5;
export const MAX_FOTO_BYTES = 10 * 1024 * 1024;
export const MAX_VIDEO_BYTES = 200 * 1024 * 1024;
export const MAX_AUDIO_BYTES = 25 * 1024 * 1024;

export function extensaoMinuscula(fileName) {
  const base = String(fileName || '').trim();
  const i = base.lastIndexOf('.');
  if (i < 0) return '';
  return base.slice(i + 1).toLowerCase();
}

export function classificarArquivo(fileName) {
  const ext = extensaoMinuscula(fileName);
  if (FOTO_EXT.has(ext)) return { kind: 'foto', ext };
  if (VIDEO_EXT.has(ext)) return { kind: 'video', ext };
  if (AUDIO_EXT.has(ext)) return { kind: 'audio', ext };
  return null;
}

export function contentTypeParaExt(ext) {
  const map = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    jfif: 'image/jpeg',
    png: 'image/png',
    heic: 'image/heic',
    mp4: 'video/mp4',
    mov: 'video/quicktime',
    avi: 'video/x-msvideo',
    mkv: 'video/x-matroska',
    webm: 'audio/webm',
    ogg: 'audio/ogg',
    mp3: 'audio/mpeg',
    m4a: 'audio/mp4',
    wav: 'audio/wav',
    opus: 'audio/opus',
  };
  return map[ext] || 'application/octet-stream';
}

export function formatBytes(n) {
  const v = Number(n) || 0;
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} KB`;
  return `${(v / (1024 * 1024)).toFixed(1)} MB`;
}

export function parseAttachmentContent(content) {
  const all = parseAllAttachmentsFromContent(content);
  return all.length ? all[0] : null;
}

/**
 * Extrai todos os anexos `__attachment__:{...}` de um conteúdo
 * (ex.: áudio + foto na mesma mensagem).
 */
export function parseAllAttachmentsFromContent(content) {
  let raw = String(content || '').trim();
  if (!raw) return [];

  raw = raw
    .replace(/&amp;/gi, '&')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/g, "'")
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>');
  raw = raw.replace(/<a\b[^>]*>([\s\S]*?)<\/a>/gi, '$1').replace(/<[^>]+>/g, '');

  const items = [];
  let searchFrom = 0;
  while (searchFrom < raw.length) {
    const markerIdx = raw.indexOf(ATTACHMENT_MARKER, searchFrom);
    if (markerIdx < 0) break;
    const after = raw.slice(markerIdx + ATTACHMENT_MARKER.length).trim();
    if (!after.startsWith('{')) {
      searchFrom = markerIdx + ATTACHMENT_MARKER.length;
      continue;
    }

    let depth = 0;
    let end = -1;
    let inString = false;
    let escape = false;
    for (let i = 0; i < after.length; i += 1) {
      const ch = after[i];
      if (inString) {
        if (escape) {
          escape = false;
          continue;
        }
        if (ch === '\\') {
          escape = true;
          continue;
        }
        if (ch === '"') inString = false;
        continue;
      }
      if (ch === '"') {
        inString = true;
        continue;
      }
      if (ch === '{') depth += 1;
      else if (ch === '}') {
        depth -= 1;
        if (depth === 0) {
          end = i;
          break;
        }
      }
    }
    if (end < 0) break;

    try {
      const json = JSON.parse(after.slice(0, end + 1));
      if (json && json.url) {
        items.push({
          url: String(json.url).trim(),
          filename: String(json.filename || 'arquivo'),
          contentType: String(json.contentType || json.content_type || ''),
          size: Number(json.size) || 0,
          key: json.key ? String(json.key) : null,
          caption: null,
        });
      }
    } catch {
      /* ignora bloco inválido */
    }
    searchFrom = markerIdx + ATTACHMENT_MARKER.length + end + 1;
  }

  if (items.length > 0) {
    let caption = null;
    const onlyIdx = raw.indexOf(ATTACHMENT_MARKER);
    if (items.length === 1 && onlyIdx >= 0) {
      const after = raw.slice(onlyIdx + ATTACHMENT_MARKER.length).trim();
      let depth = 0;
      let end = -1;
      let inString = false;
      let escape = false;
      for (let i = 0; i < after.length; i += 1) {
        const ch = after[i];
        if (inString) {
          if (escape) {
            escape = false;
            continue;
          }
          if (ch === '\\') {
            escape = true;
            continue;
          }
          if (ch === '"') inString = false;
          continue;
        }
        if (ch === '"') {
          inString = true;
          continue;
        }
        if (ch === '{') depth += 1;
        else if (ch === '}') {
          depth -= 1;
          if (depth === 0) {
            end = i;
            break;
          }
        }
      }
      if (end >= 0) {
        const cap = after.slice(end + 1).trim();
        if (cap && !cap.includes(ATTACHMENT_MARKER)) {
          caption = cap;
        }
      }
    }

    // Se houver texto antes dos anexos ou múltiplos anexos acompanhados de texto
    if (!caption) {
      const remaining = raw
        .replace(/__attachment__:\s*\{[\s\S]*?\}(?:\r?\n)?/g, '')
        .trim();
      if (remaining) {
        caption = remaining;
      }
    }

    if (caption) {
      items[0] = { ...items[0], caption };
    }
  }

  return items;
}

/** Conteúdo parece anexo (mesmo com JSON truncado na preview da lista). */
export function contentLooksLikeAttachment(content) {
  const raw = String(content || '');
  return raw.includes(ATTACHMENT_MARKER);
}

/** Anexos da mensagem: marcadores no content + attachments nativos Chatwoot. */
export function attachmentsFromMessage(msg) {
  const fromContent = parseAllAttachmentsFromContent(msg?.content);
  const native = Array.isArray(msg?.attachments) ? msg.attachments : [];
  const fromNative = native
    .map((a) => {
      if (!a || typeof a !== 'object') return null;
      const url = String(a.data_url || a.dataUrl || a.file_url || a.url || '').trim();
      if (!url) return null;
      const fileType = String(a.file_type || a.fileType || '').toLowerCase();
      const filename = String(a.filename || a.file_name || a.name || 'arquivo');
      let contentType = String(a.content_type || a.contentType || '').trim();
      if (!contentType) {
        if (fileType === 'image' || fileType === 'photo') contentType = 'image/jpeg';
        else if (fileType === 'audio') contentType = 'audio/mpeg';
        else if (fileType === 'video') contentType = 'video/mp4';
      }
      return {
        url,
        filename,
        contentType,
        size: Number(a.file_size || a.fileSize || a.size) || 0,
        key: null,
        caption: null,
      };
    })
    .filter(Boolean);

  if (!fromContent.length) return fromNative;
  if (!fromNative.length) return fromContent;

  const seen = new Set(fromContent.map((a) => a.url));
  const merged = [...fromContent];
  fromNative.forEach((a) => {
    if (!seen.has(a.url)) {
      seen.add(a.url);
      merged.push(a);
    }
  });
  return merged;
}

export function buildAttachmentContent(meta) {
  return `${ATTACHMENT_MARKER}${JSON.stringify({
    url: meta.url,
    filename: meta.filename,
    contentType: meta.contentType,
    size: meta.size,
    ...(meta.key ? { key: meta.key } : {}),
  })}`;
}

export function isImageContentType(ct, filename) {
  const c = String(ct || '').toLowerCase();
  if (c.startsWith('image/')) return true;
  const kind = classificarArquivo(filename);
  return kind?.kind === 'foto';
}

export function isVideoContentType(ct, filename) {
  const c = String(ct || '').toLowerCase();
  if (c.startsWith('video/')) return true;
  const kind = classificarArquivo(filename);
  return kind?.kind === 'video';
}

export function isAudioContentType(ct, filename) {
  const c = String(ct || '').toLowerCase();
  if (c.startsWith('audio/')) return true;
  const kind = classificarArquivo(filename);
  return kind?.kind === 'audio';
}

export function formatDuration(seconds) {
  const n = Math.max(0, Math.floor(Number(seconds) || 0));
  const m = Math.floor(n / 60);
  const s = n % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export function pickRecorderMime() {
  if (typeof MediaRecorder === 'undefined') return '';
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/mp4',
  ];
  for (const mime of candidates) {
    if (MediaRecorder.isTypeSupported?.(mime)) return mime;
  }
  return '';
}

export function extensionForAudioMime(mime) {
  const m = String(mime || '').toLowerCase();
  if (m.includes('ogg')) return 'ogg';
  if (m.includes('mp4') || m.includes('m4a')) return 'm4a';
  if (m.includes('mpeg') || m.includes('mp3')) return 'mp3';
  if (m.includes('wav')) return 'wav';
  return 'webm';
}

/** Comprime foto (exceto HEIC) via canvas — JPEG qualidade ~0.82. */
export async function optimizePhotoFile(file) {
  const ext = extensaoMinuscula(file?.name);
  if (!file || ext === 'heic') return file;
  if (!FOTO_EXT.has(ext)) return file;
  if (typeof createImageBitmap !== 'function' && typeof Image === 'undefined') return file;

  try {
    const bitmap = await createImageBitmap(file);
    const maxSide = 1920;
    let { width, height } = bitmap;
    const scale = Math.min(1, maxSide / Math.max(width, height));
    width = Math.round(width * scale);
    height = Math.round(height * scale);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(bitmap, 0, 0, width, height);
    bitmap.close?.();

    const blob = await new Promise((resolve) => {
      canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.82);
    });
    if (!blob || blob.size >= file.size) return file;
    const base = String(file.name || 'foto').replace(/\.[^.]+$/, '');
    return new File([blob], `${base}.jpg`, { type: 'image/jpeg', lastModified: Date.now() });
  } catch {
    return file;
  }
}
